import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { itTicketsAPI } from '../services/api';
import { ITTicket, ITTicketStatus } from '../types';
import { useAuth } from '../contexts/AuthContext';

const ITTickets: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tickets, setTickets] = useState<ITTicket[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ITTicketStatus | 'all'>('all');

  useEffect(() => {
    loadTickets();
  }, [statusFilter]);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const data = await itTicketsAPI.getAll(statusFilter === 'all' ? undefined : statusFilter);
      setTickets(data);
    } catch (err: any) {
      console.error('Error loading tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  const statusOptions = [
    { label: 'Svi', value: 'all' },
    { label: 'Otvoreni', value: 'open' },
    { label: 'U toku', value: 'in_progress' },
    { label: 'Rešeni', value: 'resolved' },
    { label: 'Zatvoreni', value: 'closed' }
  ];

  const problemTypeLabels: Record<string, string> = {
    hardware: 'Hardware',
    software: 'Software',
    network: 'Network',
    printer: 'Printer',
    email: 'Email',
    password: 'Password',
    access: 'Access',
    other: 'Other'
  };

  const statusLabels: Record<string, string> = {
    open: 'Otvoren',
    in_progress: 'U toku',
    resolved: 'Rešen',
    closed: 'Zatvoren'
  };

  const statusSeverity: Record<string, 'success' | 'info' | 'warning' | 'danger'> = {
    open: 'danger',
    in_progress: 'warning',
    resolved: 'info',
    closed: 'success'
  };

  const reportedByBodyTemplate = (rowData: ITTicket) => {
    return `${rowData.reportedBy.firstName} ${rowData.reportedBy.lastName}`;
  };

  const problemTypeBodyTemplate = (rowData: ITTicket) => {
    return problemTypeLabels[rowData.problemType] || rowData.problemType;
  };

  const statusBodyTemplate = (rowData: ITTicket) => {
    return (
      <Tag 
        value={statusLabels[rowData.status]} 
        severity={statusSeverity[rowData.status]}
      />
    );
  };

  const dateBodyTemplate = (rowData: ITTicket) => {
    return new Date(rowData.createdAt).toLocaleDateString('sr-RS');
  };

  const actionsBodyTemplate = (rowData: ITTicket) => {
    return (
      <Button
        icon="pi pi-eye"
        className="p-button-text"
        onClick={() => navigate(`/it-tickets/${rowData._id}`)}
        tooltip="Pogledaj detalje"
      />
    );
  };

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h1 style={{ margin: 0 }}>IT Tiketi - Upravljanje</h1>
        </div>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <span style={{ fontWeight: 'bold' }}>Filtriraj po statusu:</span>
          <Dropdown
            value={statusFilter}
            options={statusOptions}
            onChange={(e) => setStatusFilter(e.value)}
            appendTo="self"
            style={{ minWidth: '200px' }}
          />
        </div>
      </div>

      <DataTable
        value={tickets}
        loading={loading}
        paginator
        rows={10}
        emptyMessage="Nema tiketa"
        responsiveLayout="stack"
        breakpoint="768px"
      >
        <Column field="reportedBy" header="Prijavio" body={reportedByBodyTemplate} sortable />
        <Column field="problemType" header="Tip problema" body={problemTypeBodyTemplate} sortable />
        <Column field="description" header="Opis" sortable />
        <Column field="status" header="Status" body={statusBodyTemplate} sortable />
        <Column field="createdAt" header="Datum" body={dateBodyTemplate} sortable />
        <Column body={actionsBodyTemplate} header="Akcije" style={{ width: '100px' }} />
      </DataTable>
    </div>
  );
};

export default ITTickets;



